import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Autocomplete, Box, TextField, Typography} from "@mui/material";
import {TEmailTemplate} from "../../interfaces/email";
import {TOperationParticipantForListing} from "../../interfaces/operationParticipant";
import {getOperationParticipants} from "../../services/OperationService";
import {getEmailTemplatePreview, getEmailTemplateShareLink, sendPreviewEmailForTemplate} from "../../services/AdminService";
import {PrimaryBigButton, SecondarySmallButton} from "../../components/buttons/mainButton";
import LinkIcon from "@mui/icons-material/Link";
import {IconPosition} from "../../components/layoutItems/itemProps";
import EmailPreviewFrame from "./EmailPreviewFrame";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";

type EmailTemplatePreviewPanelProps = {
  template: TEmailTemplate;
  operationId?: string;
  addSuccess: (success: string) => void;
  addError: (error: string) => void;
}

export default function EmailTemplatePreviewPanel(props: EmailTemplatePreviewPanelProps): JSX.Element {
  const {template, operationId, addSuccess, addError} = props;
  const {t} = useTranslation();
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [participants, setParticipants] = useState<TOperationParticipantForListing[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState<TOperationParticipantForListing | null>(null);
  const [sending, setSending] = useState(false);
  const [emailToSend, setEmailToSend] = useState<string>("");
  const [generatingLink, setGeneratingLink] = useState(false);

  const handleCopyShareLink = (): void => {
    setGeneratingLink(true);
    getEmailTemplateShareLink(template.id, selectedParticipant?.id)
      .then(async res => {
        await navigator.clipboard.writeText(res.url);
        addSuccess(res.participantId
          ? t("email_templates.preview.share_link_copied_participant", {name: `${selectedParticipant?.firstName ?? ''} ${selectedParticipant?.lastName ?? ''}`.trim()})
          : t("email_templates.preview.share_link_copied"));
      })
      .catch(() => addError(t("global.default_error_message")))
      .finally(() => setGeneratingLink(false));
  };

  useEffect(() => {
    if (operationId) {
      getOperationParticipants(operationId, 1, 100, "", {field: "id", sort: "ASC"}, [])
        .then(res => setParticipants(res.items as unknown as TOperationParticipantForListing[]));
    }
  }, [operationId]);

  useEffect(() => {
    setLoading(true);
    setHtml(null);
    getEmailTemplatePreview(template.id, selectedParticipant?.id)
      .then(res => setHtml(res.html))
      .finally(() => setLoading(false));
  }, [template.id, selectedParticipant]);

  useEffect(() => {
    setEmailToSend(selectedParticipant?.email ?? "");
  }, [selectedParticipant]);

  const handleSend = (): void => {
    if (!selectedParticipant || !emailToSend) return;
    setSending(true);
    sendPreviewEmailForTemplate(template.id, emailToSend, selectedParticipant.id)
      .then(() => addSuccess(t("email_templates.preview.send_success")))
      .catch(() => addError(t("global.default_error_message")))
      .finally(() => setSending(false));
  }

  const filterParticipants = (options: TOperationParticipantForListing[], {inputValue}: { inputValue: string }): TOperationParticipantForListing[] => {
    const lower = inputValue.toLowerCase();
    return options.filter(p =>
      `${p.firstName ?? ''} ${p.lastName ?? ''}`.toLowerCase().includes(lower) ||
      p.id.toLowerCase().includes(lower) ||
      p.participantId.toLowerCase().includes(lower)
    );
  };

  return (
    <Box sx={{display: "flex", flexDirection: "row", height: "100%", backgroundColor: "ornament.light"}}>
      {/* Aperçu — gauche */}
      <Box sx={{flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 3, gap: 2}}>
        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
          <Box sx={{display: "flex", alignItems: "center", gap: 1, minWidth: 0}}>
            <VisibilityIcon sx={{color: "neutral.main", width: 18, height: 18}} />
            <Typography variant="body1" sx={{fontWeight: "bold", color: "neutral.main"}}>
              {t("email_templates.preview.preview")}
            </Typography>
          </Box>
          <SecondarySmallButton
            label={t("email_templates.preview.share_link_button")}
            action={handleCopyShareLink}
            loading={generatingLink}
            icon={LinkIcon}
            position={IconPosition.LEFT}
          />
        </Box>
        <EmailPreviewFrame
          html={html}
          status={loading ? "loading" : (html !== null ? "ready" : "error")}
          sender={template.mailSender ?? t("email_templates.preview.default_sender")}
          subject={template.subject ?? t("email_templates.preview.default_subject")}
          title={template.name}
          minHeight={400}
          fill
        />
      </Box>

      {/* Control — droite */}
      {operationId && (
        <Box sx={{width: 320, borderLeft: "1px solid", borderColor: "ornament.dark", p: 3, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto"}}>
          <Typography variant="body2" sx={{fontWeight: "bold", color: "neutral.main"}}>
            {t("email_templates.preview.participant_label")}
          </Typography>
          <Autocomplete
            options={participants}
            getOptionLabel={(p): string => `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim()}
            isOptionEqualToValue={(option, value): boolean => option.id === value.id}
            value={selectedParticipant}
            onChange={(_, value): void => setSelectedParticipant(value)}
            filterOptions={filterParticipants}
            renderInput={(params): JSX.Element => (
              <TextField {...params} placeholder={t("email_templates.preview.participant_placeholder")} />
            )}
          />
          {selectedParticipant && (
            <>
              <Typography variant="body2" sx={{fontWeight: "bold", color: "neutral.main", mt: 1}}>
                {t("email_templates.preview.send_to")}
              </Typography>

              <TextField
                value={emailToSend}
                onChange={(e): void => setEmailToSend(e.target.value)}
              />
              <PrimaryBigButton
                label={t("email_templates.preview.send_preview_email_button")}
                action={handleSend}
                loading={sending}
                icon={SendIcon}
                position={IconPosition.LEFT}
              />
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
